import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Stack,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ActionCheckboxItem from './ActionCheckboxItem';
import intl from 'components/i18n/ReactIntlWrapper';
import type { ModuleActionConfigAndSchema } from 'axiosApi/models/module-action-config-and-schema';

interface ModuleAccordionProps {
  moduleName: string;
  actions: { [key: string]: ModuleActionConfigAndSchema } | null;
  isActive: boolean;
  onToggleActive: (moduleName: string, active: boolean) => void;
  selectedActions: Map<string, boolean>;
  onActionSelect: (moduleName: string, actionName: string, selected: boolean) => void;
  onActionClick: (moduleName: string, actionName: string) => void;
}

/**
 * ModuleAccordion - Renders an expandable accordion for a single notification module
 * 
 * Structure:
 * - Header: Module name + Active/Inactive toggle switch
 * - Body: List of action checkboxes
 * 
 * When the module toggle is activated, all actions under it are marked as checked
 */
const ModuleAccordion: React.FC<ModuleAccordionProps> = ({
  moduleName,
  actions,
  isActive,
  onToggleActive,
  selectedActions,
  onActionSelect,
  onActionClick,
}) => {
  const actionEntries = Object.entries(actions);


  const handleToggleActive = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation(); // Prevent accordion from toggling
    const newActiveState = event.target.checked;
    onToggleActive(moduleName, newActiveState);
    
    // When toggling module active state, update all actions under this module
    actionEntries.forEach(([actionName]) => {
      onActionSelect(moduleName, actionName, newActiveState);
    });
  };

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${moduleName}-content`}
        id={`${moduleName}-header`}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            pr: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" color="primary">
              {intl.get(`${moduleName}`)}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={handleToggleActive}
                onClick={(e) => e.stopPropagation()}
                color={isActive ? 'success' : 'warning'}
              />
            }
            label={
              <Typography variant="body2" color={isActive ? 'success.main' : 'error.main'} sx={{ fontWeight: 600 }}>
                {isActive ? intl.get('common.active') : intl.get('common.inactive')}
              </Typography>
            }
            onClick={(e) => e.stopPropagation()}
            sx={{ m: 0 }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={0.5}>
          {actionEntries.map(([actionName, action]) => {
            const key = `${moduleName}.${actionName}`;
            const isChecked = selectedActions.get(key) || false;

            return (
              <ActionCheckboxItem
                key={key}
                actionName={actionName}
                schema={action.schema}
                isChecked={isChecked}
                onCheck={(checked) => onActionSelect(moduleName, actionName, checked)}
                onClick={() => onActionClick(moduleName, actionName)}
              />
            );
          })}
          {actionEntries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {intl.get('notification.configuration.noActionsAvailable')}
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default ModuleAccordion;
